import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../packages/auth-client';
import { Button, Input } from '../packages/ui';
import { getErrorMessage } from '../packages/api-client';
import { formatDeletionDate } from '../packages/api-client/account-deletion-api';
import { DELETION_NOTICE_KEY } from '../components/DeleteAccountPanel';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../hooks/useTheme';
import { safeReturnTo } from '../utils/returnTo';

/**
 * LoginPage — two-step 2FA login.
 *
 * Step 1: Email + password → server validates, sends OTP, returns session_token.
 * Step 2: 6-digit OTP code → server verifies, returns JWT pair → redirect.
 */
export function LoginPage() {
    usePageTitle('Sign in');
    const { theme } = useTheme();
    const { login, verifyOTP, resendOTP, otpPending } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Step 1 state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Step 2 state
    const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
    const otpRefs = [
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
    ];

    const [isLoading, setIsLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [rememberDevice, setRememberDevice] = useState(false);

    // Deleting your account signs you out, which unmounts the screen that
    // showed when deletion lands. The date is stashed on the way out and
    // repeated here, along with the fact that signing back in cancels it.
    const [deletionNotice] = useState<string | null>(() => {
        try {
            return sessionStorage.getItem(DELETION_NOTICE_KEY);
        } catch {
            return null;
        }
    });

    // Focus first OTP box when step 2 appears
    useEffect(() => {
        if (otpPending) {
            setTimeout(() => otpRefs[0].current?.focus(), 50);
        }
    }, [otpPending]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resend cooldown timer
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);

    // Once they are back in, the in-app banner owns the message.
    const clearDeletionNotice = () => {
        try {
            sessionStorage.removeItem(DELETION_NOTICE_KEY);
        } catch {
            // Private-mode storage refusal — nothing to clear.
        }
    };

    // ?returnTo= (set by ProtectedRoute) is the page the visitor just asked
    // for, so it wins over an older stashed postLoginRedirect. Both the
    // password-only and the OTP step land here; the query survives step 2
    // because the URL never changes between them.
    const consumePostLoginRedirect = (): string => {
        const returnTo = safeReturnTo(searchParams.get('returnTo'));
        const next = sessionStorage.getItem('postLoginRedirect');
        if (next) sessionStorage.removeItem('postLoginRedirect');
        if (returnTo) return returnTo;
        if (next) return next;
        // /home resolves the right landing once the user is loaded: restricted
        // users go to their project, everyone else to the dashboard.
        return '/home';
    };

    // ── Step 1 submit ──────────────────────────────────────────────────────────
    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const result = await login(email, password);
            if (result === 'authenticated') {
                clearDeletionNotice();
                navigate(consumePostLoginRedirect());
            }
            // 'otp_required' → the context sets otpPending=true, UI re-renders to step 2
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    };

    // ── Step 2: OTP digit input handling ──────────────────────────────────────
    const handleDigitChange = (index: number, value: string) => {
        // Strip non-digits and take only the last character typed
        const digit = value.replace(/\D/g, '').slice(-1);
        const next = [...otpDigits];
        next[index] = digit;
        setOtpDigits(next);
        if (digit && index < 5) {
            otpRefs[index + 1].current?.focus();
        }
    };

    const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
            otpRefs[index - 1].current?.focus();
        }
        // Allow paste to fill all boxes
        if (e.key === 'v' && (e.metaKey || e.ctrlKey)) return;
    };

    const handleOTPPaste = (e: React.ClipboardEvent) => {
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 6) {
            e.preventDefault();
            setOtpDigits(pasted.split(''));
            otpRefs[5].current?.focus();
        }
    };

    // ── Step 2 submit ──────────────────────────────────────────────────────────
    const handleOTPSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const code = otpDigits.join('');
        if (code.length !== 6) {
            toast.error('Please enter the full 6-digit code');
            return;
        }
        setIsLoading(true);
        try {
            await verifyOTP(code, rememberDevice);
            clearDeletionNotice();
            navigate(consumePostLoginRedirect());
        } catch (err) {
            toast.error(getErrorMessage(err));
            setOtpDigits(['', '', '', '', '', '']);
            otpRefs[0].current?.focus();
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        try {
            await resendOTP();
            setResendCooldown(60);
            toast.success('A new code has been sent');
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="login-container">
            <div className="login-card hud-corner">
                <div style={{ marginBottom: '1rem' }}><KaziAtlasLogo size={46} onLight={theme === 'light'} /></div>

                {deletionNotice && (
                    <div
                        role="status"
                        style={{
                            border: '1px solid var(--warn)',
                            background: 'color-mix(in srgb, var(--warn) 14%, transparent)',
                            borderRadius: 'var(--radius-md, 8px)',
                            padding: '0.75rem 1rem',
                            marginBottom: '1rem',
                            fontSize: '0.85rem',
                            lineHeight: 1.5,
                            color: 'var(--ink)',
                            textAlign: 'left',
                        }}
                    >
                        <strong>
                            Your account is scheduled for deletion on{' '}
                            {formatDeletionDate(deletionNotice)}.
                        </strong>
                        <br />
                        Sign in before then to cancel it.
                    </div>
                )}

                {!otpPending ? (
                    /* ── Step 1: Email + Password ── */
                    <>
                        <h1 className="login-title">Welcome back</h1>
                        <p className="login-subtitle">Sign in to continue to your workspace</p>

                        <form className="login-form" onSubmit={handlePasswordSubmit}>
                            <Input
                                id="email"
                                type="email"
                                label="Email address"
                                placeholder="admin@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                            />
                            <Input
                                id="password"
                                type="password"
                                label="Password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                            />
                            <div style={{ textAlign: 'right', marginTop: '-0.5rem' }}>
                                <Link
                                    to="/forgot-password"
                                    style={{ fontSize: '0.8125rem', color: 'var(--accent)', fontWeight: 500, textDecoration: 'none' }}
                                >
                                    Forgot password?
                                </Link>
                            </div>
                            <Button
                                type="submit"
                                variant="primary"
                                size="md"
                                isLoading={isLoading}
                                className="form-button"
                            >
                                Continue
                            </Button>
                        </form>
                    </>
                ) : (
                    /* ── Step 2: OTP Code ── */
                    <>
                        <h1 className="login-title">Check your email</h1>
                        <p className="login-subtitle">
                            We sent a 6-digit code to <strong style={{ color: 'var(--ink)' }}>{email}</strong>
                        </p>

                        <form className="login-form" onSubmit={handleOTPSubmit}>
                            {/* OTP digit boxes */}
                            <div
                                className="hud-corner login-otp-fields"
                                onPaste={handleOTPPaste}
                            >
                                {otpDigits.map((digit, i) => (
                                    <input
                                        key={i}
                                        ref={otpRefs[i]}
                                        id={`otp-${i}`}
                                        aria-label={`Verification code digit ${i + 1}`}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleDigitChange(i, e.target.value)}
                                        onKeyDown={(e) => handleDigitKeyDown(i, e)}
                                        autoComplete="one-time-code"
                                        style={{
                                            width: '100%',
                                            minWidth: 0,
                                            height: '3.5rem',
                                            textAlign: 'center',
                                            fontSize: '1.5rem',
                                            fontWeight: 600,
                                            letterSpacing: 0,
                                            borderRadius: 'var(--radius-sm)',
                                            border: digit
                                                ? '2px solid var(--accent)'
                                                : '2px solid var(--border)',
                                            background: 'var(--surface)',
                                            color: 'var(--ink)',
                                            outline: 'none',
                                            transition: 'border-color 0.15s',
                                        }}
                                    />
                                ))}
                            </div>

                            {/* Remember this device checkbox */}
                            <label
                                htmlFor="remember-device"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    margin: '0 0 1rem',
                                    fontSize: '0.875rem',
                                    color: 'var(--ink-muted)',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                }}
                            >
                                <input
                                    id="remember-device"
                                    type="checkbox"
                                    checked={rememberDevice}
                                    onChange={(e) => setRememberDevice(e.target.checked)}
                                    style={{ accentColor: 'var(--accent)', width: '1rem', height: '1rem', cursor: 'pointer' }}
                                />
                                Remember this device for 20 days
                            </label>

                            <Button
                                type="submit"
                                variant="primary"
                                size="md"
                                isLoading={isLoading}
                                className="form-button"
                            >
                                Verify code
                            </Button>
                        </form>

                        <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                            Didn't receive a code?{' '}
                            <button
                                type="button"
                                onClick={handleResend}
                                disabled={resendCooldown > 0}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: resendCooldown > 0 ? 'default' : 'pointer',
                                    color: resendCooldown > 0 ? 'var(--ink-muted)' : 'var(--accent)',
                                    fontWeight: 600,
                                    fontSize: 'inherit',
                                    padding: 0,
                                }}
                            >
                                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                            </button>
                        </p>
                    </>
                )}
            </div>

            {!otpPending && (
                <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
                    Don't have an account?{' '}
                    <Link to="/register" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>Create workspace</Link>
                </p>
            )}
        </div>
    );
}
